import { useState } from "react";
import { updateAgent, type Agent, type UpdateAgentInput } from "../api/client.js";

const MODELS = [
  { value: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5" },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
  { value: "gpt-4o", label: "GPT-4o (OpenAI)" },
  { value: "ollama/llama3", label: "Llama 3 (Ollama)" },
  { value: "gemini-pro", label: "Gemini Pro" },
];

const POLICIES = [
  { value: "read-only", label: "Read-Only", desc: "Low-risk tools only" },
  { value: "standard", label: "Standard", desc: "Low + medium risk tools" },
  { value: "privileged", label: "Privileged", desc: "All tools including high risk" },
];

export function AgentConfigEditor({
  agent,
  onSave,
}: {
  agent: Agent;
  onSave: (updated: Agent) => void;
}) {
  const [form, setForm] = useState<UpdateAgentInput>({
    name: agent.name,
    description: agent.description ?? "",
    systemPrompt: agent.systemPrompt,
    model: agent.model,
    toolPolicyProfile: agent.toolPolicyProfile,
    enabled: agent.enabled,
    tokenBudget: (agent as any).tokenBudget ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);

  function update(field: keyof UpdateAgentInput, value: unknown) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setError("");
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const updated = await updateAgent(agent.id, form);
      onSave(updated);
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Name */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Name
        </label>
        <input
          type="text"
          value={form.name ?? ""}
          onChange={(e) => update("name", e.target.value)}
          className="input mt-2 text-sm"
          maxLength={200}
        />
      </section>

      {/* Description */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Description
        </label>
        <input
          type="text"
          value={form.description ?? ""}
          onChange={(e) => update("description", e.target.value)}
          className="input mt-2 text-sm"
          placeholder="Optional description..."
        />
      </section>

      {/* Model + Provider */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Model
        </label>
        <select
          value={form.model ?? agent.model}
          onChange={(e) => update("model", e.target.value)}
          className="input mt-2 text-sm"
        >
          {MODELS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </section>

      {/* Tool Policy */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Tool Policy Profile
        </label>
        <div className="mt-3 space-y-2">
          {POLICIES.map((p) => (
            <label
              key={p.value}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
                form.toolPolicyProfile === p.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-ink-100 hover:bg-surface-1"
              }`}
            >
              <input
                type="radio"
                name="toolPolicy"
                value={p.value}
                checked={form.toolPolicyProfile === p.value}
                onChange={() => update("toolPolicyProfile", p.value)}
                className="h-4 w-4 text-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-ink-800">{p.label}</span>
                <p className="text-xs text-ink-500">{p.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* System Prompt */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          System Prompt
        </label>
        <textarea
          value={form.systemPrompt ?? ""}
          onChange={(e) => update("systemPrompt", e.target.value)}
          className="input mt-2 min-h-[10rem] resize-y font-mono text-xs leading-relaxed"
        />
        <p className="mt-1 text-2xs text-ink-400">
          Content is scanned by PromptGuard before saving. Injection attempts will be rejected.
        </p>
      </section>

      {/* Token Budget */}
      <section className="card p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Token Budget (per conversation)
        </label>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="number"
            value={form.tokenBudget ?? ""}
            onChange={(e) =>
              update("tokenBudget", e.target.value ? parseInt(e.target.value, 10) : null)
            }
            className="input w-40 text-sm"
            placeholder="Unlimited"
            min={1}
          />
          <span className="text-xs text-ink-400">Leave blank for unlimited</span>
        </div>
      </section>

      {/* Enable/Disable */}
      <section className="card p-5">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-ink-400">
              Agent Status
            </label>
            <p className="mt-1 text-xs text-ink-500">
              Disabled agents cannot be used in conversations
            </p>
          </div>
          <button
            onClick={() => update("enabled", !form.enabled)}
            className={`relative h-6 w-11 rounded-full transition-colors ${
              form.enabled ? "bg-emerald-500" : "bg-ink-300"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                form.enabled ? "left-[1.375rem]" : "left-0.5"
              }`}
            />
          </button>
        </div>
      </section>

      {/* Metadata (read-only) */}
      <section className="card p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Details</h3>
        <dl className="mt-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs text-ink-400">Agent ID</dt>
            <dd className="mt-0.5 font-mono text-xs text-ink-700">{agent.id}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-400">Created</dt>
            <dd className="mt-0.5 text-xs text-ink-700">
              {new Date(agent.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </dd>
          </div>
        </dl>
      </section>

      {/* Save bar */}
      {dirty && (
        <div className="sticky bottom-0 flex items-center justify-between rounded-xl border border-ink-100 bg-white px-5 py-3 shadow-lg">
          <span className="text-xs text-ink-500">Unsaved changes</span>
          <div className="flex items-center gap-2">
            {error && <span className="text-xs text-danger">{error}</span>}
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary text-xs"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
